<template>
  <div>
    <h1 class="text-xl font-bold mb-4">Set Auditor Index</h1>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <svg class="animate-spin h-10 w-10 network-button-color" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
      </svg>
    </div>

    <div v-else>
      <div class="mt-4">
        <button
            @click="saveOrder"
            class="block rounded-md px-3 py-2 text-center text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-gray-600 text-white hover:bg-gray-500 focus-visible:outline-gray-600 m-1"
            v-show="notSaved"
        >
          Save Order
        </button>
      </div>

      <div ref="scrollWrap" class="max-h-[70vh] overflow-auto border border-gray-300 rounded">
        <table class="min-w-full table-auto border-collapse">
          <thead>
          <tr class="bg-gray-100 border-b">
            <th class="px-4 py-2 border w-12 text-center">#</th>
            <th class="px-1 py-1 border text-left whitespace-nowrap w-1">Key</th>
            <th class="px-4 py-2 border text-left">Text</th>
          </tr>
          </thead>

          <draggable
              tag="tbody"
              :list="data"
              item-key="id"
              handle=".cursor-move"
              @start="onDragStart"
              @end="onDragEnd"
              @change="onListChange"
              :options="dragOptions"
          >
            <template #item="{ element }">
              <tr class="bg-white border-b hover:bg-gray-50" :data-debug-order="element.order">
                <td class="px-4 py-2 border w-12 text-center cursor-move">{{ element.auditor_index }}</td>
                <td class="border text-left cursor-move whitespace-nowrap px-1 py-1 w-1">{{ element.question_key }}</td>
                <td class="px-4 py-2 border text-left cursor-move">{{ element.text }}</td>
              </tr>
            </template>
          </draggable>
        </table>
      </div>

      <div class="mt-4">
        <button
            @click="saveOrder"
            class="block rounded-md px-3 py-2 text-center text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-gray-600 text-white hover:bg-gray-500 focus-visible:outline-gray-600 m-1"
            v-show="notSaved"
        >
          Save Order
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import axios from "axios";

export default {
  name: "QuestionSetAuditorIndexComponent",
  components: { draggable },
  data() {
    return {
      data: [],
      isLoading: false,
      notSaved: false,

      _dragRAF: null,
      _pointerX: 0,
      _pointerY: 0,
      _isDragging: false,

      dragOptions: {
        animation: 150,
        forceFallback: true,
        fallbackOnBody: false,
        ghostClass: 'drag-ghost',
        scroll: false,
      }
    }
  },
  computed: {
    questionSetId() {
      return this.$route.params.question_set_id ? parseInt(this.$route.params.question_set_id) : null;
    },
  },
  created() {
    this.getQuestions();
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.$router.beforeEach(this.handleRouteLeave);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.$router.beforeHooks = this.$router.beforeHooks.filter(
        (hook) => hook !== this.handleRouteLeave
    );
    this._stopAutoScroll();
    document.removeEventListener('pointermove', this._onPointerMove);
    document.removeEventListener('touchmove', this._onTouchMove);
  },
  methods: {
    handleBeforeUnload(e) {
      if (this.notSaved) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    },
    handleRouteLeave(to, from, next) {
      if (this.notSaved) {
        const answer = window.confirm('You have unsaved changes. Are you sure you want to leave the page?');
        if (!answer) return next(false);
      }
      next();
    },
    getQuestions() {
      this.isLoading = true;
      axios.get(`/member-firm/question-set/auditor-index/show/${this.questionSetId}`)
          .then((res) => {
            this.data = res.data.data.map((q, index) => ({
              ...q,
              auditor_index: q.auditor_index || index + 1,
            })).sort((a, b) => a.auditor_index - b.auditor_index);
          })
          .catch((error) => {
            console.error(error);
            this.$notify({ title: "Unsuccessfully", text: error.message });
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    onListChange() {
      this.notSaved = true;
      this.data.forEach((item, index) => {
        item.auditor_index = index + 1;
      });
    },
    saveOrder() {
      this.isLoading = true;
      const reordered = this.data.map((q, index) => ({
        id: q.id,
        auditor_index: index + 1,
      }));

      axios.put(`/member-firm/question-set/auditor-index/reorder`, {
        question_set_id: this.questionSetId,
        questions: reordered,
      }).then(response => {
        this.$notify({ type: "success", title: "Success", text: response.data.message || "Order saved successfully." });
        this.getQuestions();
        this.notSaved = false;
      }).catch(error => {
        console.error(error);
        const message = error.response?.data?.message || error.message || "Unknown error";
        this.$notify({ title: "Error", text: message });
        this.notSaved = false;
      }).finally(() => {
        this.isLoading = false;
      });
    },
    onDragStart() {
      this._isDragging = true;
      document.addEventListener('pointermove', this._onPointerMove, { passive: true });
      document.addEventListener('touchmove', this._onTouchMove, { passive: true });
      this._startAutoScroll();
    },

    onDragEnd() {
      this._isDragging = false;
      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('touchmove', this._onTouchMove);
      this._stopAutoScroll();
      this._pointerX = 0;
      this._pointerY = 0;
    },

    _onPointerMove(e) {
      if (e && typeof e.clientY === 'number') {
        this._pointerX = e.clientX;
        this._pointerY = e.clientY;
      }
    },

    _onTouchMove(e) {
      if (e && e.touches && e.touches[0]) {
        this._pointerX = e.touches[0].clientX;
        this._pointerY = e.touches[0].clientY;
      }
    },
    _startAutoScroll() {
      const threshold = 60;
      const maxSpeed = 50;

      const step = () => {
        if (!this._isDragging) {
          this._dragRAF = null;
          return;
        }

        const wrap = this.$refs.scrollWrap;
        let containerRect;
        let canScrollUp = false;
        let canScrollDown = false;

        if (wrap) {
          containerRect = wrap.getBoundingClientRect();
          canScrollUp = wrap.scrollTop > 0;
          canScrollDown = wrap.scrollTop + wrap.clientHeight < wrap.scrollHeight - 1;
        } else {
          containerRect = { top: 0, bottom: window.innerHeight };
          canScrollUp = window.scrollY > 0;
          const docH = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
          canScrollDown = (window.scrollY + window.innerHeight) < (docH - 1);
        }

        let targetRect = null;
        const ghost = document.querySelector('.drag-ghost') || document.querySelector('.sortable-ghost');
        if (ghost) {
          try { targetRect = ghost.getBoundingClientRect(); } catch (err) { targetRect = null; }
        }
        if (!targetRect && this._pointerY) {
          targetRect = { top: this._pointerY, bottom: this._pointerY };
        }
        if (!targetRect) {
          this._dragRAF = requestAnimationFrame(step);
          return;
        }

        const topZone = containerRect.top + threshold;
        const bottomZone = containerRect.bottom - threshold;

        if (targetRect.bottom > bottomZone && canScrollDown) {
          const dist = targetRect.bottom - bottomZone;
          const speed = Math.min(maxSpeed, Math.max(1, Math.round((dist / threshold) * maxSpeed)));
          if (wrap) wrap.scrollTop += speed;
          else window.scrollBy(0, speed);
        } else if (targetRect.top < topZone && canScrollUp) {
          const dist = Math.max(1, topZone - targetRect.top);
          const speed = Math.min(maxSpeed, Math.max(1, Math.round((dist / threshold) * maxSpeed)));
          if (wrap) wrap.scrollTop -= speed;
          else window.scrollBy(0, -speed);
        }

        this._dragRAF = requestAnimationFrame(step);
      };

      if (!this._dragRAF) this._dragRAF = requestAnimationFrame(step);
    },
    _stopAutoScroll() {
      if (this._dragRAF) cancelAnimationFrame(this._dragRAF);
      this._dragRAF = null;
    },
  }
}
</script>
